import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Shield, CheckCircle } from 'lucide-react';
import logger from '../utils/logger';
import PropTypes from 'prop-types';
import { SessionManager } from '../utils/session-manager';

const SignupModal = ({ isOpen, onClose, onSignupSuccess }) => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    captcha: '',
    step: 'SIGNUP'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return `+91${cleaned}`;
  };

  // Indian mobile number validation
  const isValidIndianMobile = (phone) => {
    // Indian mobile numbers: 10 digits starting with 6, 7, 8, or 9
    const indianMobileRegex = /^[6-9]\d{9}$/;
    return indianMobileRegex.test(phone);
  };

  // Name validation (letters, spaces, and common name characters)
  const isValidName = (name) => {
    // Allow letters, spaces, hyphens, apostrophes, and dots
    const nameRegex = /^[a-zA-Z\s\-'\.]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    if (!isValidIndianMobile(formData.phoneNumber)) {
      setError('Please enter a valid Indian mobile number (10 digits starting with 6, 7, 8, or 9)');
      setIsLoading(false);
      return;
    }

    const formattedPhone = formatPhoneNumber(formData.phoneNumber);

    try {
      // Appointment limit check commented out for testing
      /*
      const lastAppointment = await SessionManager.checkLastAppointment(formattedPhone);
      if (lastAppointment) {
        const appointmentDate = new Date(lastAppointment.createdAt);
        const hoursSinceLastAppointment = (Date.now() - appointmentDate.getTime()) / (1000 * 60 * 60);
        
        if (hoursSinceLastAppointment < 48) {
          const nextAvailable = new Date(appointmentDate.getTime() + (48 * 60 * 60 * 1000));
          setError(`You can book your next appointment after ${nextAvailable.toLocaleString()}`);
          setIsLoading(false);
          return;
        }
      }
      */

      // Generate captcha
      const captcha = generateCaptcha();
      setCaptchaCode(captcha);
      
      setSuccessMessage('Please verify the captcha below');
      setFormData(prev => ({ ...prev, step: 'VERIFY' }));
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err) {
      logger.error('Failed to generate captcha:', err);
      setError('Failed to generate captcha. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (formData.captcha.toUpperCase() === captchaCode) {
        const formattedPhone = formatPhoneNumber(formData.phoneNumber);
        SessionManager.createSession(formattedPhone);
        setSuccessMessage('Verification successful! Redirecting...');
        
        // Small delay to show success message
        setTimeout(() => {
          onSignupSuccess(formattedPhone);
          onClose();
        }, 1000);
      } else {
        setError('Invalid captcha. Please try again.');
        // Generate new captcha on wrong input
        const newCaptcha = generateCaptcha();
        setCaptchaCode(newCaptcha);
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="signup-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center pt-20 pb-1 px-1 sm:pt-24 sm:pb-2 sm:px-2 md:pt-24 md:pb-4 md:px-4 bg-black/50 backdrop-blur-sm"
        >
          <div
            className="relative w-full max-w-[95vw] sm:max-w-sm md:max-w-md bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden transform scale-100 transition-all duration-300 max-h-[75vh] sm:max-h-[80vh] md:max-h-[85vh] lg:max-h-[90vh] overflow-y-auto"
          >
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="p-4 sm:p-6 lg:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {formData.step === 'SIGNUP' ? 'Enter Your Mobile Number' : 'Verify Captcha'}
                </h2>
                <p className="mt-2 text-sm sm:text-base text-gray-600">
                  {formData.step === 'SIGNUP' 
                    ? 'We will verify your mobile number'
                    : 'Enter the captcha code shown below'}
                </p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-3 bg-red-50 text-red-700 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}

              {successMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-3 bg-green-50 text-green-700 rounded-lg text-sm flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  {successMessage}
                </motion.div>
              )}

              {formData.step === 'SIGNUP' ? (
                <form onSubmit={handleSignup} className="space-y-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                      <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      <span className="ml-1 sm:ml-2 text-gray-500 text-sm sm:text-base">+91</span>
                    </div>
                    <input
                      type="tel"
                      maxLength="10"
                      value={formData.phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setFormData(prev => ({ ...prev, phoneNumber: value }));
                      }}
                      className="w-full pl-16 sm:pl-20 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Enter 10-digit number"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium
                             hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Generating...' : 'Continue'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerify} className="space-y-6">
                  {/* Captcha Display */}
                  <div className="text-center">
                    <div className="inline-block p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
                      <div className="text-2xl font-mono font-bold text-gray-800 tracking-wider">
                        {captchaCode}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">Enter the code above</p>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                      <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={formData.captcha}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setFormData(prev => ({ ...prev, captcha: value }));
                      }}
                      className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      placeholder="Enter captcha code"
                      maxLength={5}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium
                             hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Verifying...' : 'Verify Captcha'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

SignupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSignupSuccess: PropTypes.func.isRequired
};

export default SignupModal; 